import { EmptySection1 } from "components/empty";
import React from "react";
import { WORK_IN_PROGRESS } from "icons/src";
import useCheckPermission from "hooks/src/useCheckPermissons";
import { usePermissions } from "contexts/permission";

/**
 * Higher-Order Function that wraps components with permission checks
 * @param {React.Component} WrappedComponent - The component to wrap
 * @param {Object} permissionConfig - Permission configuration
 * @param {Object} permissionConfig.condition - Permission condition object
 * @param {Object} permissionConfig.complexCondition - Complex condition object for evaluatePermission
 * @param {string} permissionConfig.collection - Collection name for permission check
 * @param {string} permissionConfig.action - Action type (default: 'read')
 * @param {string} permissionConfig.field - Specific field to check access for
 * @param {boolean} permissionConfig.requireFullAccess - Whether full access is required
 * @param {React.Component} permissionConfig.fallback - Custom fallback component
 * @param {boolean} permissionConfig.silent - If true, renders nothing instead of fallback
 * @returns {React.Component} - Permission-wrapped component
 */
export const withPermissions = (
  WrappedComponent,
  condition,
  silent = true,
  fallback,
) => {
  const PermissionWrappedComponent = (props) => {
    const { canMutate } = useCheckPermission(condition);

    if (!canMutate) {
      if (silent) {
        return null;
      }

      if (fallback) {
        return fallback;
      }

      return null;
    }

    return <WrappedComponent {...props} />;
  };

  // Set display name for better debugging                                                                                        │
  PermissionWrappedComponent.displayName = `withPermissions(${
    WrappedComponent.displayName || WrappedComponent.name || "Component"
  })`;
  return PermissionWrappedComponent;
};

/**
 * Convenience HOFs for common permission patterns
 */

// Collection access
export const withCollectionAccess =
  (collection, action = "read") =>
  (WrappedComponent, fallback) =>
    withPermissions(WrappedComponent, { collection, action, fallback });

// Field access
export const withFieldAccess =
  (collection, field, action = "read") =>
  (WrappedComponent, fallback) =>
    withPermissions(WrappedComponent, { collection, action, field, fallback });

// Silent rendering (no fallback, just returns null)
export const withSilentPermissions = (WrappedComponent, permissionConfig) =>
  withPermissions(WrappedComponent, { ...permissionConfig, silent: true });

/**
 * Example usage in your component files:
 *
 * // Basic usage
 * export default withPermissions(JobTotalAllocatedHoursStat, {
 *   collection: 'jobs',
 *   action: 'read',
 *   field: 'allocated_hours'
 * });
 *
 * // Complex condition
 * export default withPermissions(BudgetSection, {
 *   complexCondition: {
 *     type: "multiple",
 *     conditions: [{ collection: "budget", action: "read" }],
 *     operator: "OR"
 *   }
 * });
 *
 * // Silent rendering
 * export default withSilentPermissions(OptionalComponent, {
 *   condition: { collection: "optional", action: "read" }
 * });
 */
